import { getRecordValue } from 'src/modules/shared/utils/record.util';
import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import { useMemo, useState } from 'react';
import { Typography } from '@material-tailwind/react';
import * as ExcelJS from 'exceljs';
import {
  extractHeadersFromWorksheet,
  parseWorksheetRows,
  type ExcelRowData,
  normalizeStringList,
} from 'src/modules/communications/utils/excel-import.util';
import { Link } from '@tanstack/react-router';
import {
  PiX,
  PiUploadSimple,
  PiDownloadSimple,
  PiTable,
  PiWarning,
  PiInfo,
  PiCheckCircle,
  PiXCircle,
} from 'react-icons/pi';

import { useFunnels } from 'src/modules/shared/hooks/api/crm/funnels/funnels';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useBulkCreateConversations } from 'src/modules/communications/hooks/api/conversations/bulk-conversations';
import { useCustomFieldDefinitions } from 'src/modules/shared/hooks/api/custom-fields/definitions';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users';

import {
  type BulkConversationPassthroughCreateParams,
  type BulkOperationResponse,
  type BulkSingleConversationCreateParams,
  CustomFieldEntityType,
  type BulkOperationItemValidation,
} from 'src/api/generated';

import Button from 'src/modules/shared/components/atoms/buttons/button';
import Loading from 'src/modules/shared/components/atoms/loading';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { FileInput } from 'src/modules/shared/components/atoms/inputs/file-input';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { Table } from 'src/modules/shared/components/molecules/table/table';
import { Panel } from 'src/modules/shared/components/molecules/panel';

/**
 * Page to import conversations from an Excel file into a selected funnel.
 * Handles Excel parsing, column-to-field mapping, validation and bulk creation.
 */
const ImportConversationsPage: React.FC = () => {
  const [excelData, setExcelData] = useState<ExcelRowData[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [uploadResult, setUploadResult] = useState<{
    success?: boolean;
    message?: string;
    response?: BulkOperationResponse;
  }>({});
  const [unmatchedEmails, setUnmatchedEmails] = useState<string[]>([]);
  const [unmatchedStatusNames, setUnmatchedStatusNames] = useState<string[]>(
    []
  );

  const funnelsQuery = createQuery().orderByDesc('createdAt').build();
  const { funnels, loading: loadingFunnels } = useFunnels(funnelsQuery);

  const { users, loading: loadingUsers } = useAllPersonAndSystemUsers();
  const { user: currentUser } = useCurrentUser();

  const [selectedFunnelId, setSelectedFunnelId] = useState<number | null>(null);

  const isFunnelSelected = !!selectedFunnelId;
  const funnelIdQuery = useMemo(() => {
    if (!selectedFunnelId) return {};

    return createQuery()
      .equals('funnels.some.funnelId', selectedFunnelId)
      .equals('entityType', CustomFieldEntityType.FUNNEL_CONVERSATION)
      .orderByAsc('createdAt')
      .take(100)
      .build();
  }, [selectedFunnelId]);

  const {
    definitions: conversationDefinitions,
    loading: loadingConversationDefinitions,
  } = useCustomFieldDefinitions({
    entityType: CustomFieldEntityType.FUNNEL_CONVERSATION,
    ...funnelIdQuery,
    enabled: isFunnelSelected,
  });
  const {
    definitions: contactDefinitions,
    loading: loadingContactDefinitions,
  } = useCustomFieldDefinitions({
    entityType: CustomFieldEntityType.CONTACT,
    ...funnelIdQuery,
    enabled: isFunnelSelected,
  });
  const definitions = [...conversationDefinitions, ...contactDefinitions];

  const statusQuery = useMemo(() => {
    if (!selectedFunnelId) return {};

    return createQuery()
      .equals('funnelId', selectedFunnelId)
      .orderByAsc('position')
      .build();
  }, [selectedFunnelId]);
  const { statuses } = useFunnelStatuses(statusQuery, isFunnelSelected);

  const { createBulkConversations, loading: isUploading } =
    useBulkCreateConversations({
      onSuccess: (data) => {
        setUploadResult({
          success: true,
          // TODO: check when implementing i18n
          message: `Importación exitosa.`,
          response: data,
        });
        setExcelData([]);
        setHeaders([]);
        setMappings({});
        setUnmatchedEmails([]);
      },
      onError: (error) => {
        console.error('Error uploading data:', error);
        setUploadResult({
          success: false,
          // TODO: check when implementing i18n
          message: `Error: ${error.message || 'Error al importar conversaciones'}`,
        });
      },
    });

  /**
   * Parses the provided Excel file into JSON rows and extracts the header set.
   * Updates `excelData`, `headers` and resets mappings and validation state.
   */
  const handleFileChange = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = evt.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        const worksheet = workbook.worksheets[0];
        const extractedHeaders = extractHeadersFromWorksheet(worksheet);
        const jsonData = parseWorksheetRows(worksheet, extractedHeaders);

        if (jsonData.length > 0 || extractedHeaders.length > 0) {
          setExcelData(jsonData);
          setHeaders(extractedHeaders);
          setMappings({});
          setUploadResult({});
          setUnmatchedEmails([]);
          setUnmatchedStatusNames([]);
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setUploadResult({
          success: false,
          message:
            // TODO: check when implementing i18n
            'Error al analizar el archivo Excel. Por favor, asegúrate de que sea un archivo Excel válido.',
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  /**
   * Generates and downloads an Excel template with headers and a sample row.
   * Column widths are auto-sized based on header and sample values.
   */
  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Plantilla');

    const headersRow = [
      'Nombre del contacto',
      'Apellido del contacto',
      'Teléfono del contacto',
      'Correo del contacto',
      'Correo del ejecutivo asignado',
      'Estado asignado',
    ];

    worksheet.addRow(headersRow);

    const firstStatusName =
      statuses.length > 0 ? statuses[0].name : 'Nombre del estado a asignar';
    const currentUserEmail =
      nonEmptyString(currentUser?.email) ?? 'Correo del usuario a asignar';

    const sampleRow = [
      'Nombre de ejemplo',
      'Apellido de ejemplo',
      '+569XXXXXXXX',
      'correo.de.ejemplo@gmail.com',
      currentUserEmail,
      firstStatusName,
    ];

    worksheet.addRow(sampleRow);

    const rowsForSizing = [headersRow, sampleRow];
    rowsForSizing[0].forEach((_h, idx) => {
      let maxLength = 10;
      for (const row of rowsForSizing) {
        const cellValue = row[idx];
        const value = cellValue ? cellValue.toString() : '';
        maxLength = Math.max(maxLength, value.length + 2);
      }
      const col = worksheet.getColumn(idx + 1);
      col.width = maxLength;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla-importacion-conversaciones.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  /**
   * Updates the column-to-field mapping and triggers targeted validations
   * when mapping email or funnel status fields.
   */
  const handleMappingChange = (header: string, value: string) => {
    setMappings((prev) => {
      const newMappings = {
        ...prev,
        [header]: value,
      };
      if (value === 'conversation.userEmail') {
        setTimeout(() => {
          validateAndSetUnmatchedEmails(newMappings);
        }, 0);
      } else if (value === 'conversation.funnelStatusName') {
        setTimeout(() => {
          validateAndSetUnmatchedStatusNames(newMappings);
        }, 0);
      } else if (prev[header] === 'conversation.userEmail') {
        setUnmatchedEmails([]);
      } else if (prev[header] === 'conversation.funnelStatusName') {
        setUnmatchedStatusNames([]);
      }

      return newMappings;
    });
  };

  /**
   * Finds a user's id by email (case-insensitive, trimmed).
   */
  const findUserIdByEmail = (email: string): number | undefined => {
    const normalizedEmail = email.trim().toLowerCase();
    const user = users.find(
      (user) => user.email.toLowerCase() === normalizedEmail
    );

    return user?.id;
  };

  /**
   * Validates mapped executive emails and collects those not matching any user.
   * Updates `unmatchedEmails` with invalid addresses.
   */
  const validateAndSetUnmatchedEmails = (currentMappings = mappings) => {
    if (!excelData.length) {
      setUnmatchedEmails([]);
      return;
    }

    const emailsToValidate: string[] = [];
    const userEmailHeader = Object.keys(currentMappings).find(
      (header) => currentMappings[header] === 'conversation.userEmail'
    );

    if (!userEmailHeader) {
      setUnmatchedEmails([]);
      return;
    }

    excelData.forEach((row) => {
      const email = row[userEmailHeader];
      if (email && typeof email === 'string' && email.trim() !== '') {
        emailsToValidate.push(email.trim());
      }
    });

    const invalidEmails = emailsToValidate.filter((email) => {
      const userId = findUserIdByEmail(email);
      return userId === undefined;
    });

    setUnmatchedEmails(invalidEmails);
  };

  /**
   * Returns the funnel status id that exactly matches the provided name.
   */
  const findFunnelStatusIdByName = (name: string): number | undefined => {
    if (statuses.length === 0) return undefined;
    const normalized = name.trim();
    const match = statuses.find((s) => s.name === normalized);
    return match?.id;
  };

  /**
   * Validates mapped funnel status names and collects those not matching any status.
   * Updates `unmatchedStatusNames` with invalid names.
   */
  const validateAndSetUnmatchedStatusNames = (currentMappings = mappings) => {
    if (!excelData.length) {
      setUnmatchedStatusNames([]);
      return;
    }

    const namesToValidate: string[] = [];
    const statusNameHeader = Object.keys(currentMappings).find(
      (header) => currentMappings[header] === 'conversation.funnelStatusName'
    );

    if (!statusNameHeader) {
      setUnmatchedStatusNames([]);
      return;
    }

    excelData.forEach((row) => {
      const name = row[statusNameHeader];
      if (name && typeof name === 'string' && name.trim() !== '') {
        namesToValidate.push(name.trim());
      }
    });

    const invalidNames = namesToValidate.filter((name) => {
      const statusId = findFunnelStatusIdByName(name);
      return statusId === undefined;
    });

    setUnmatchedStatusNames(invalidNames);
  };

  /**
   * Converts parsed Excel rows and user-defined mappings into API payload format
   * for bulk conversation creation. Also ensures validation state is refreshed.
   * Returns null if no funnel is selected.
   */
  const convertToBulkFormat =
    (): BulkConversationPassthroughCreateParams | null => {
      if (!selectedFunnelId) return null;
      validateAndSetUnmatchedEmails();
      validateAndSetUnmatchedStatusNames();

      const conversations = excelData.map((row) => {
        const conversation: BulkSingleConversationCreateParams = {
          funnelId: selectedFunnelId,
          customFields: {},
        };

        let contact: BulkSingleConversationCreateParams['contact'];
        const ensureContact = () =>
          (contact ??= {
            firstName: '',
            lastName: '',
            phones: [],
            emails: [],
            nationalId: '',
            companyPosition: '',
            customFields: {},
          });

        const pendingPhones: (string | number | null | undefined)[] = [];
        const pendingEmails: (string | number | null | undefined)[] = [];

        const applyCustomField = (mapping: string, value: string | number) => {
          const cleanedValue = typeof value === 'string' ? value.trim() : value;
          if (cleanedValue === '') return;
          if (mapping.startsWith('contact.customFields.')) {
            const target = ensureContact();
            target.customFields ??= {};
            target.customFields[mapping.replace('contact.customFields.', '')] =
              cleanedValue;
          } else if (mapping.startsWith('conversation.customFields.')) {
            conversation.customFields ??= {};
            conversation.customFields[
              mapping.replace('conversation.customFields.', '')
            ] = cleanedValue;
          }
        };

        headers.forEach((header) => {
          const mapping = mappings[header];
          const value = getRecordValue(row, header);
          if (!mapping || value == null) return;

          switch (mapping) {
            case 'contact.firstName':
              ensureContact().firstName = String(value).trim();
              return;
            case 'contact.lastName':
              ensureContact().lastName = String(value).trim();
              return;
            case 'contact.nationalId':
              ensureContact().nationalId = String(value).trim();
              return;
            case 'contact.companyPosition':
              ensureContact().companyPosition = String(value).trim();
              return;
            case 'contact.emails':
              pendingEmails.push(value);
              return;
            case 'contact.phones':
              pendingPhones.push(value);
              return;
            case 'conversation.userEmail': {
              const userId = findUserIdByEmail(String(value));
              if (userId) conversation.userId = userId;
              return;
            }
            case 'conversation.contactId': {
              if (value === '') return;
              const numeric = Number(value);
              if (!Number.isNaN(numeric)) conversation.contactId = numeric;
              return;
            }
            case 'conversation.funnelStatusName': {
              const statusId = findFunnelStatusIdByName(String(value));
              if (statusId) conversation.funnelStatusId = statusId;
              return;
            }
            default:
              applyCustomField(mapping, value);
          }
        });

        if (contact) {
          const cleanedPhones = normalizeStringList(pendingPhones);
          const cleanedEmails = normalizeStringList(pendingEmails);
          contact.phones = [...(contact.phones ?? []), ...cleanedPhones];
          contact.emails = [...(contact.emails ?? []), ...cleanedEmails];
          conversation.contact = contact;
        }

        return conversation;
      });

      return { conversations };
    };

  /**
   * Validates the current selection and mappings, builds the bulk payload
   * and triggers the bulk creation mutation.
   */
  const handleSubmit = async () => {
    if (!selectedFunnelId) {
      setUploadResult({
        success: false,
        // TODO: check when implementing i18n
        message: 'Por favor, selecciona un embudo.',
      });
      return;
    }

    if (Object.keys(mappings).length === 0) {
      setUploadResult({
        success: false,
        // TODO: check when implementing i18n
        message: 'Por favor, mapea al menos una columna.',
      });
      return;
    }

    const bulkData = convertToBulkFormat();
    if (!bulkData) return;

    try {
      await createBulkConversations(bulkData);
    } catch (error) {
      console.error('Error processing bulk creation:', error);
    }
  };

  /**
   * Builds the selectable mapping options for contact fields, conversation
   * fields and custom fields derived from current definitions.
   */
  const getMappingOptions = () => {
    // TODO: check when implementing i18n
    const baseContactFields = [
      { value: 'contact.firstName', label: '👤 Nombre' },
      { value: 'contact.lastName', label: '👤 Apellido' },
      { value: 'contact.nationalId', label: '👤 RUT' },
      { value: 'contact.companyPosition', label: '👤 Cargo' },
      { value: 'contact.emails', label: '👤 Correo' },
      { value: 'contact.phones', label: '👤 Teléfono' },
    ];

    // TODO: check when implementing i18n
    const baseConversationFields = [
      { value: 'conversation.userEmail', label: '💬 Email del Ejecutivo' },
      {
        value: 'conversation.contactId',
        label: '💬 ID de contacto existente',
      },
      {
        value: 'conversation.funnelStatusName',
        label: '💬 Estado del Embudo (por nombre)',
      },
    ];

    const customFieldOptions = definitions.map((def) => {
      const entityPrefix =
        def.entityType === CustomFieldEntityType.CONTACT ? '👤' : '💬';
      const displayName = nonEmptyString(def.fieldLabel) ?? def.fieldKey;

      // TODO: check when implementing i18n
      const label = `${entityPrefix} ${displayName}`;

      return {
        value: `${def.entityType === CustomFieldEntityType.CONTACT ? 'contact' : 'conversation'}.customFields.${def.fieldKey}`,
        label: label,
      };
    });

    return [
      ...baseContactFields,
      ...baseConversationFields,
      ...customFieldOptions,
    ];
  };

  /**
   * Columns configuration for the issues table in the upload response.
   */
  const itemIssuesColumns = [
    { header: 'Índice', accessor: 'index' },
    {
      header: 'Errores',
      accessor: 'errors',
      render: (_: unknown, row: BulkOperationItemValidation) => (
        <div>
          {row.errors.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1 text-danger-700">
              {row.errors.map((error, idx) => (
                <li key={idx}>{error.message}</li>
              ))}
            </ul>
          ) : (
            <Typography className="text-success-700">&nbsp;</Typography>
          )}
        </div>
      ),
    },
    {
      header: 'Advertencias',
      accessor: 'warnings',
      render: (_: unknown, row: BulkOperationItemValidation) => (
        <div>
          {row.warnings.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1 text-warning-700">
              {row.warnings.map((warning, idx) => (
                <li key={idx}>{warning.message}</li>
              ))}
            </ul>
          ) : (
            <Typography className="text-neutral-500">&nbsp;</Typography>
          )}
        </div>
      ),
    },
  ];

  /**
   * Renders a summary of the bulk operation including counts and details.
   */
  const renderResponseSummary = () => {
    const { response } = uploadResult;
    if (!response) return null;

    return (
      <div className="mt-6 space-y-6">
        <Alert
          variant="primary"
          message={
            nonEmptyString(response.message) ?? uploadResult.message ?? ''
          }
          icon={
            uploadResult.success ? (
              <PiCheckCircle className="h-5 w-5" />
            ) : (
              <PiXCircle className="h-5 w-5" />
            )
          }
        />
        <div>
          <Typography variant="h6" className="mb-3">
            {/* TODO: check when implementing i18n */}
            Resumen de la importación
          </Typography>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            <div className="bg-success-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-success-700 mb-1">
                <PiCheckCircle className="h-5 w-5" />
                {/* TODO: check when implementing i18n */}
                <Typography className="font-medium">Exitosos</Typography>
              </div>
              <Typography className="text-2xl font-bold text-success-700">
                {response.report.counts.successful}
              </Typography>
              {response.report.counts.successful > 0 && (
                <Typography className="text-xs text-success-700 mt-1">
                  {/* TODO: check when implementing i18n */}
                  En procesamiento, espera unos minutos
                </Typography>
              )}
            </div>

            <div className="bg-warning-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-warning-700 mb-1">
                <PiWarning className="h-5 w-5" />
                {/* TODO: check when implementing i18n */}
                <Typography className="font-medium">Advertencias</Typography>
              </div>
              <Typography className="text-2xl font-bold text-warning-700">
                {response.report.counts.warnings}
              </Typography>
              {response.report.counts.warnings > 0 && (
                <Typography className="text-xs text-warning-700 mt-1">
                  {/* TODO: check when implementing i18n */}
                  Las advertencias no impiden el procesamiento
                </Typography>
              )}
            </div>

            <div className="bg-warning-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-warning-700 mb-1">
                <PiInfo className="h-5 w-5" />
                {/* TODO: check when implementing i18n */}
                <Typography className="font-medium">Duplicados</Typography>
              </div>
              <Typography className="text-2xl font-bold text-warning-700">
                {response.report.counts.duplicates}
              </Typography>
              {response.report.counts.duplicates > 0 && (
                <Typography className="text-xs text-warning-700 mt-1">
                  {/* TODO: check when implementing i18n */}
                  Se procesarán como una sola entidad
                </Typography>
              )}
            </div>

            <div className="bg-danger-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-danger-700 mb-1">
                <PiXCircle className="h-5 w-5" />
                {/* TODO: check when implementing i18n */}
                <Typography className="font-medium">Errores</Typography>
              </div>
              <Typography className="text-2xl font-bold text-danger-700">
                {response.report.counts.errors}
              </Typography>
              {response.report.counts.errors > 0 && (
                <Typography className="text-xs text-danger-700 mt-1">
                  {/* TODO: check when implementing i18n */}
                  No serán procesados
                </Typography>
              )}
            </div>

            <div className="bg-neutral-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-neutral-700 mb-1">
                <PiTable className="h-5 w-5" />
                {/* TODO: check when implementing i18n */}
                <Typography className="font-medium">Total</Typography>
              </div>
              <Typography className="text-2xl font-bold text-neutral-700">
                {response.report.counts.total}
              </Typography>
              <Typography className="text-xs text-neutral-700 mt-1">
                {/* TODO: check when implementing i18n */}
                Items encontrados en el archivo
              </Typography>
            </div>
          </div>
        </div>

        {response.report.batchErrors.length > 0 && (
          <ExpandableSection
            title="Errores generales"
            isInitiallyExpanded={false}
          >
            <div className="bg-danger-50 p-3 rounded-lg">
              <ul className="list-disc pl-5 text-danger-700 space-y-2">
                {response.report.batchErrors.map((error, idx) => (
                  <li key={idx}>
                    <div className="font-medium">
                      {error.field || 'Error global'}
                    </div>
                    <div>{error.message}</div>
                    {<div className="text-xs">Código: {error.code}</div>}
                  </li>
                ))}
              </ul>
            </div>
          </ExpandableSection>
        )}

        {response.report.itemsIssues.filter(
          (issue) => issue.errors.length > 0 || issue.warnings.length > 0
        ).length > 0 && (
          <ExpandableSection
            title="Detalles por ítem"
            isInitiallyExpanded={false}
          >
            <Table
              columns={itemIssuesColumns}
              data={response.report.itemsIssues.filter(
                (issue) => issue.errors.length > 0 || issue.warnings.length > 0
              )}
              keyExtractor={(item) => `item-issue-${item.index}`}
              variant="primary"
              size="xs"
              emptyStateMessage="No hay problemas con los ítems"
            />
          </ExpandableSection>
        )}
      </div>
    );
  };

  if (loadingFunnels || loadingUsers) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading />
      </div>
    );
  }

  /**
   * Renders a warning panel listing email addresses that could not be mapped
   * to existing users.
   */
  const renderEmailValidationWarning = () => {
    if (unmatchedEmails.length === 0) return null;

    return (
      <div className="mb-4 mt-2">
        <Alert
          variant="warning"
          appearance="soft"
          icon={<PiWarning className="h-5 w-5" />}
          message={
            <div>
              {/* TODO: check when implementing i18n */}
              <div className="flex items-center">
                <span>
                  Al menos un usuario no fue encontrado, se va a omitir la
                  asignación de esa conversación
                </span>
              </div>
              <details className="mt-2 text-sm">
                <summary className="cursor-pointer font-medium text-warning-700 hover:text-warning-800">
                  {/* TODO: check when implementing i18n */}
                  Ver emails no encontrados ({unmatchedEmails.length})
                </summary>
                <ul className="mt-1 list-disc pl-5 text-warning-700">
                  {unmatchedEmails.map((email, index) => (
                    <li key={index} className="mt-1 text-xs">
                      {email}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          }
        />
      </div>
    );
  };

  /**
   * Renders a warning panel listing funnel status names that did not match
   * any existing status.
   */
  const renderFunnelStatusValidationWarning = () => {
    if (unmatchedStatusNames.length === 0) return null;

    return (
      <div className="mb-4 mt-2">
        <Alert
          variant="warning"
          appearance="soft"
          icon={<PiWarning className="h-5 w-5" />}
          message={
            <div>
              <div className="flex items-center">
                <span>
                  Al menos un estado del embudo no fue encontrado, se va a
                  omitir la asignación de ese estado
                </span>
              </div>
              <details className="mt-2 text-sm">
                <summary className="cursor-pointer font-medium text-warning-700 hover:text-warning-800">
                  Ver estados no encontrados ({unmatchedStatusNames.length})
                </summary>
                <ul className="mt-1 list-disc pl-5 text-warning-700">
                  {unmatchedStatusNames.map((name, index) => (
                    <li key={index} className="mt-1 text-xs">
                      {name}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          }
        />
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <Typography variant="h4">
          {/* TODO: check when implementing i18n */}
          Importar Conversaciones
        </Typography>
        <Link
          to="/communications"
          search={{ view: 'pipeline', phoneVersion: 'v1' }}
          className="p-2 rounded-full hover:bg-neutral-100 transition-colors"
          // TODO: check when implementing i18n
          title="Cerrar"
        >
          <PiX className="w-5 h-5" />
        </Link>
      </div>

      <Panel className="mb-6">
        <div className="p-4 space-y-6">
          <div>
            <Typography className="font-medium mb-2">
              {/* TODO: check when implementing i18n */}
              1. Selecciona un embudo para importar conversaciones
            </Typography>

            <Select
              options={[
                // TODO: check when implementing i18n
                { value: '', content: 'Selecciona un embudo' },
                ...funnels.map((funnel) => ({
                  value: funnel.id.toString(),
                  content: funnel.name,
                })),
              ]}
              value={selectedFunnelId?.toString() ?? ''}
              onChange={(e) => setSelectedFunnelId(Number(e.target.value))}
            />
          </div>

          {selectedFunnelId && (
            <div>
              <Typography className="font-medium mb-2">
                {/* TODO: check when implementing i18n */}
                2. Sube tu archivo Excel
              </Typography>

              <div className="flex items-center gap-3">
                <FileInput
                  onChange={handleFileChange}
                  containerClassName="flex justify-start"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                />
                <Button
                  onClick={handleDownloadTemplate}
                  variant="neutral"
                  appearance="soft"
                  isIconOnly
                  // TODO: check when implementing i18n
                  aria-label="Descargar ejemplo"
                  title="Descargar ejemplo"
                >
                  <PiDownloadSimple className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {headers.length > 0 && (
            <div>
              <Typography className="font-medium mb-3">
                {/* TODO: check when implementing i18n */}
                3. Mapea tus columnas de Excel a los campos de conversación
              </Typography>

              {loadingConversationDefinitions || loadingContactDefinitions ? (
                <div className="py-6 flex items-center justify-center">
                  <Loading />
                </div>
              ) : (
                <>
                  {renderEmailValidationWarning()}
                  {renderFunnelStatusValidationWarning()}

                  <div className="space-y-3">
                    {headers.map((header) => (
                      <div key={header} className="flex items-center gap-4">
                        <div className="w-1/3">
                          <Typography>{header}</Typography>
                        </div>
                        <div className="w-2/3">
                          <Select
                            options={[
                              // TODO: check when implementing i18n
                              {
                                value: '',
                                content: 'No importar esta columna',
                              },
                              ...getMappingOptions().map((option) => ({
                                value: option.value,
                                content: option.label,
                              })),
                            ]}
                            value={mappings[header] || ''}
                            onChange={(e) =>
                              handleMappingChange(header, e.target.value)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    <Button
                      onClick={handleSubmit}
                      disabled={isUploading || !selectedFunnelId}
                      loading={isUploading}
                    >
                      <PiUploadSimple className="w-4 h-4 mr-2" />
                      {/* TODO: check when implementing i18n */}
                      Importar Conversaciones
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {uploadResult.message && !uploadResult.response && (
            <Alert
              variant="primary"
              message={uploadResult.message}
              icon={
                uploadResult.success ? (
                  <PiCheckCircle className="h-5 w-5" />
                ) : (
                  <PiXCircle className="h-5 w-5" />
                )
              }
            />
          )}

          {uploadResult.response && renderResponseSummary()}

          {excelData.length > 0 && (
            <ExpandableSection
              title="Vista previa de datos"
              isInitiallyExpanded={false}
            >
              <div className="overflow-x-auto">
                <Table
                  columns={headers.map((header) => ({
                    header,
                    accessor: header,
                    render: (value) => (
                      <Typography className="text-sm">
                        {value?.toString() ?? ''}
                      </Typography>
                    ),
                  }))}
                  data={excelData.slice(0, 5)}
                  keyExtractor={(_item: ExcelRowData) => {
                    return `preview-row-${Math.random().toString(36).substring(2, 11)}`;
                  }}
                  variant="neutral"
                  size="xs"
                  emptyStateMessage="No hay datos para mostrar"
                />
              </div>
              {excelData.length > 5 && (
                <Typography className="text-sm text-neutral-500 mt-2 text-center">
                  {/* TODO: check when implementing i18n */}
                  Mostrando las primeras 5 filas de {excelData.length} totales
                </Typography>
              )}
            </ExpandableSection>
          )}
        </div>
      </Panel>
    </div>
  );
};

export default ImportConversationsPage;
